/**
 * LoginFormInput Component
 * Extracted from LoginPage.tsx
 * Reusable form input with icon for login form
 */

import React from 'react';
import { LucideIcon } from 'lucide-react';
import { cn } from '@/ui/theme/ThemeContext';

interface LoginFormInputProps {
  id: string;
  type: 'text' | 'password';
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  icon: LucideIcon;
  dir?: React.HTMLAttributes<HTMLInputElement>['dir'];
  autoFocus?: boolean;
  autoComplete?: string;
  error?: string;
  describedBy?: string;
  helperText?: string;
}

export const LoginFormInput: React.FC<LoginFormInputProps> = ({
  id,
  type,
  label,
  value,
  onChange,
  placeholder,
  icon: Icon,
  dir,
  autoFocus,
  autoComplete,
  error,
  describedBy,
  helperText,
}) => {
  const errorId = error ? describedBy || `${id}-error` : undefined;
  const helperId = helperText ? `${id}-help` : undefined;
  const describedByIds = [helperId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="min-w-0">
      <label
        htmlFor={id}
        className="mb-2 block text-sm font-semibold text-zinc-700 dark:text-zinc-200"
      >
        {label}
      </label>
      <div className="relative min-w-0 max-w-full">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon className="h-5 w-5 text-zinc-300" />
        </div>
        <input
          id={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          dir={dir}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          className={cn(
            "block h-11 min-h-[44px] w-full min-w-0 max-w-full overflow-hidden text-ellipsis rounded-md border bg-zinc-950/[0.52] py-2 pl-10 pr-3 leading-5 text-white shadow-[0_8px_22px_rgba(15,23,42,0.10)] backdrop-blur transition-all duration-200",
            "placeholder:text-zinc-400",
            "focus:outline-none focus:ring-2 focus:ring-blue-400/60 dark:focus:ring-cyan-300/60",
            error ? "border-red-500 dark:border-red-400" : "border-white/[0.14]"
          )}
          style={{ maxWidth: '100%', unicodeBidi: 'plaintext' }}
          placeholder={placeholder}
          required
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={describedByIds}
        />
      </div>
      {helperText && (
        <p
          id={helperId}
          className="mt-2 text-xs leading-5 text-zinc-500 dark:text-zinc-400"
        >
          {helperText}
        </p>
      )}
      {error && (
        <p
          id={errorId}
          className="mt-2 text-sm text-red-600 dark:text-red-400"
        >
          {error}
        </p>
      )}
    </div>
  );
};
